import { Fragment } from "react";
import { Alert, Card, Col, Container, Row } from "react-bootstrap";
import InvoiceLineItemModel from "../../Shared/Models/InvoiceLineItemModel";
import InvoiceModel from "../../Shared/Models/InvoiceModel";
import ReceiptModel from "../../Shared/Models/ReceiptModel";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";
import ApplicationModel from "../Models/ApplicationModel";
import Examination from "../Models/Examination";

interface IProps {
    application: ApplicationModel;
    receipt: ReceiptModel;
    examination: Examination;
    examinationDates: string;
    fullName: string;
    emailAddress: string;
}

export function InvoiceApplicationReceived(props: IProps) {
    const totalFees = props.receipt?.invoices?.map(i => i.amountDue()).reduce((a, b) => a + b, 0);

    const getAssessmentType = () => {
        return props.examination.isArticleBased ? "ABCC Assessment" : "Examination";
    }
    const getNextSteps = (invoice: InvoiceModel) => {
        return props.examination.isArticleBased ? getArticleBasedExamNextStepsText() : getSecureExamNextStepsText(invoice);
    }

    const getSecureExamNextStepsText = (invoice: InvoiceModel) => {
        return (<Fragment>
            <Card className="mb-4" border="pp-secondary-grey">
                <Card.Body>
                    <Card.Title className="mb-4 font-weight-bolder">
                        <span className="text-pp-primary">Pre-Examination Follow-Up</span>
                    </Card.Title>
                    {props.examination.requiresFellowshipTraining &&
                        <Row>
                            <Col xs={12} xl={3}>
                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">Training Documentation</div>
                            </Col>
                            <Col xs={12} xl={9} className="mb-3">
                                <span>If you completed a fellowship program, you will be expected to email your credentials administrator documentation of your fellowship training when requested.  It should include the exact start and end dates of your training and will need your program director's signature.  Your application will not be reviewed unless your administrator receives verification of your training when requested.</span>
                            </Col>
                        </Row>}
                    <Row>
                        <Col xs={12} xl={3}>
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Exam Requirements</div>
                        </Col>
                        <Col xs={12} xl={9} className="mb-3">
                            <span>
                                Submission of your application through this software is NOT an indication that your credentials meet the requirements for admission to the examination. Requirements for application can be found in the Information for Applicants (IFA) publication. THE IFA, examination content specifications, and the examination format and scoring documents can all be found on our website at:
                                <a className="text-pp-primary" onClick={() => openInfoUrl(invoice.infoUrl)}>
                                    &nbsp;{invoice.infoUrl} <i className="las la-external-link-alt"></i>
                                </a>
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} xl={3}>
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Exam Next Steps</div>
                        </Col>
                        <Col xs={12} xl={9} className="mb-3">
                            <span>All candidates who have been approved to sit for an examination will receive an email with scheduling instructions two months prior to the administration of the exam.</span>
                        </Col>
                    </Row>
                    {(props.application.isNmaRequested || props.application.isAdaRequested) &&
                        <Row>
                            <Col xs={12} xl={3}>
                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">Accommodations Requested</div>
                            </Col>
                            <Col xs={12} xl={9} className="mb-3">
                                <span hidden={!props.application.isNmaRequested}>You have requested accommodations due to being pregnant or a nursing mother. You will receive an email from the Board informing you of next steps.<br /><br /></span>
                                <span hidden={!props.application.isAdaRequested}>You have requested accommodations due to a disability. You will receive an email from the Board informing you of next steps.</span>
                            </Col>
                        </Row>}
                </Card.Body>
            </Card>
        </Fragment>);
    }

    const getArticleBasedExamNextStepsText = () => {
        return (<Fragment>
            <Card className="mb-4" border="pp-secondary-grey">
                <Card.Body>
                    <Card.Title className="mb-4 font-weight-bolder">
                        <span className="text-pp-primary">ABCC Assessment Follow-Up</span>
                    </Card.Title>
                    <Row>
                        <Col xs={12} xl={3}>
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">ABCC Requirements</div>
                        </Col>
                        <Col xs={12} xl={9} className="mb-3">
                            <span>
                                Submission of your application through this software is NOT an indication that your credentials meet the requirements for the article-based pathway.  If audited, you will need to submit documentation of the completed requirements.
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} xl={3}>
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">ABCC Next Steps</div>
                        </Col>
                        <Col xs={12} xl={9} className="mb-3">
                            <span>
                            You can begin the article exams in the Physician Portal once the application has been approved. 
                            </span>
                        </Col>
                    </Row>
                    {(props.application.isNmaRequested || props.application.isAdaRequested) &&
                        <Row>
                            <Col xs={12} xl={3}>
                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">Accommodations Requested</div>
                            </Col>
                            <Col xs={12} xl={9} className="mb-3">
                                <span hidden={!props.application.isNmaRequested}>You have requested accommodations due to being pregnant or a nursing mother. You will receive an email from the Board informing you of next steps.<br /><br /></span>
                                <span hidden={!props.application.isAdaRequested}>You have requested accommodations due to a disability. You will receive an email from the Board informing you of next steps.</span>
                            </Col>
                        </Row>}
                </Card.Body>
            </Card>
        </Fragment>);
    }

    const openInfoUrl = (infoUrl: string) => {
        window.open(infoUrl, "_blank")
    }

    const invoiceLineItemDiscount = (lineItem: InvoiceLineItemModel) => {
        if (lineItem.discountAmount > 0) return (
            <Row>
                <Col xs={12} md={8} lg={6} xl={5}>
                    <span className="font-weight-bolder mb-1 pp-font-size-smaller">{lineItem.discountDescription}</span>
                    <span className="float-right">${lineItem.unitPrice} - {lineItem.discountAmount}</span>
                    <hr className="mt-1 mb-1"/>
                </Col>
            </Row>
        );
        else return;
    }

    const invoiceLineItem = (lineItem: InvoiceLineItemModel) => {
        return (<Fragment>
                    <Row>
                        <Col xs={12} md={8} lg={6} xl={5}>
                            <span className="font-weight-bolder mb-1 pp-font-size-smaller">{lineItem.description}</span>
                            <span className="float-right">${lineItem.amount}</span>
                            <hr className="mt-1 mb-1"/>
                        </Col>
                    </Row>
                    {invoiceLineItemDiscount(lineItem)}
                </Fragment>)
    }

    const invoiceDetail = (invoice: InvoiceModel) => {
        return (<Card className="mb-4" border="pp-secondary-grey">
                    <Card.Body>
                        <Card.Title className="mb-4 font-weight-bolder">
                            <span className="text-pp-primary">Billing</span>
                            <Row>
                                <Col xs={12} lg={3} className="mb-3">
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">Invoice #</div>
                                    {invoice.invoiceNumber}
                                </Col>
                                <Col xs={12} lg={3} className="mb-3">
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">Invoice Date</div>
                                    {invoice.createDate && getDateWithOffset(invoice.createDate).toLocaleDateString()}
                                </Col>
                            </Row>
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Order #</div>
                            {invoice.invoiceNumber}
                            <Row>
                                <Col xs={12} lg={3} className="mb-3">
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">Name</div>
                                    {props.fullName}
                                </Col>
                                <Col xs={12} lg={3} className="mb-3">
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">Email</div>
                                    {props.emailAddress}
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={3} className="mb-3">
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">{getAssessmentType()}</div>
                                    {props.examination.title}
                                    <br></br>
                                    <small>{invoice.moduleSelection}{invoice.formattedCnpType}</small>
                                </Col>
                                <Col xs={12} lg={3} className="mb-3">
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">{getAssessmentType()} Date</div>
                                    {props.examinationDates}
                                </Col>
                            </Row>
                        </Card.Title>
                        {invoice.lineItems.map(_ => { return (
                            invoiceLineItem(_)
                        )})}
                    </Card.Body>
                </Card>);
    } 

    return (
        <Container>
            <h3 className="text-pp-secondary font-weight-bold">ABPN Application Payment Receipt</h3>
            <hr></hr>
            <div className="mb-4">7 Parkway North, Deerfield IL 60015 | Phone 847.229.6500 | Fax 847.229.6600</div>
            <Alert variant="pp-alert-success font-weight-bold">A payment receipt has been sent to {props.emailAddress} </Alert>
            <Card className="mb-4" border="pp-secondary-grey">
                <Card.Body>
                    <Card.Title className="mb-4 font-weight-bolder">
                        <span className="text-pp-primary">Order</span>
                    </Card.Title>

                    <Row>
                        <Col xs={12} lg={3} className="mb-3">
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Name</div>
                            {props.fullName}
                        </Col>
                        <Col xs={12} lg={3} className="mb-3">
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Email</div>
                            {props.emailAddress}
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} lg={3} className="mb-3">
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">{getAssessmentType()}</div>
                            {props.examination.title}
                            <br></br>
                            {/* <small>{props.application.moduleSelection}{props.application.formattedCnpType}</small> */}
                        </Col>
                        <Col xs={12} lg={3} className="mb-3">
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">{getAssessmentType()} Date</div>
                            {props.examinationDates}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {props.receipt?.cardHolderName &&
                <Card className="mb-4" border="pp-secondary-grey">
                    <Card.Body>
                        <Card.Title className="mb-4 font-weight-bolder">
                            <span className="text-pp-primary">Payment</span>
                        </Card.Title>
                        <Row>
                            <Col xs={12} lg={3} className="mb-3">
                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">Payment Method</div>
                                {props.receipt?.paymentMethod}
                            </Col>
                            <Col xs={12} lg={3} className="mb-3">
                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">Card Holder Name</div>
                                {props.receipt?.cardHolderName}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>}

            <Card className="mb-4" border="pp-secondary-grey">
                <Card.Body>
                    <Card.Title className="mb-4 font-weight-bolder">
                        <span className="text-pp-primary">Billing</span>
                    </Card.Title>
                    {props.receipt?.invoices?.map(_ => { return (
                        <Fragment>
                            {invoiceDetail(_)}
                            {getNextSteps(_)}
                        </Fragment>
                    )
                    })}
                    <Row className="mb-4">
                        <Col xs={12} md={8} lg={6} xl={5}>
                            <span className="font-weight-bolder mb-1 pp-font-size-smaller">Total </span>
                            <span className="float-right">${totalFees}</span>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col xs={12} md={8} lg={6} xl={5}>
                            <span className="font-weight-bolder mb-1 pp-font-size-smaller">Paid </span>
                            <span className="float-right">${-1 * props.receipt.amountPaid}
                            </span>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col xs={12} md={8} lg={6} xl={5}>
                            <span className="font-weight-bolder mb-1 pp-font-size-smaller">Due</span>
                            <span className="float-right">$0</span>
                            <hr className="mt-1 mb-1" />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            
        </Container>
    );
}

export default InvoiceApplicationReceived;